import React, { useEffect, useState } from "react";
import { api, formatApiError, money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { Plus, Trash2, Pencil, BarChart3, Instagram, Facebook, Phone, Palette, Moon, Sun, Globe } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../../components/ConfirmProvider";
import { useCompany } from "../../contexts/CompanyContext";

const EMPTY_CREATE = {
  company: {
    name: "", slug: "", primary_color: "#0A0A0A", logo_url: "",
    phone: "", whatsapp: "", instagram: "", facebook: "", description: "", active: true,
  },
  admin_email: "", admin_password: "", admin_name: "",
};

export default function SuperAdminCompanies() {
  const confirm = useConfirm();
  const {
    platformColor, setPlatformColor,
    heroImageUrl, setHeroImageUrl,
    heroImageUrlDark, setHeroImageUrlDark,
    faviconUrl, setFaviconUrl,
    loginBannerImage, setLoginBannerImage,
    platformName, setPlatformName,
    landingHeadingLine1, setLandingHeadingLine1,
    landingHeadingLine2, setLandingHeadingLine2,
    landingChips, setLandingChips,
    reloadPlatformSettings,
  } = useCompany();
  const [list, setList] = useState([]);
  const [m, setM] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editOpen, setEditOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [metricsData, setMetricsData] = useState(null);
  const [metricsCompany, setMetricsCompany] = useState(null);
  // Buffer local para que el usuario edite el color sin aplicarlo hasta
  // presionar "Guardar". Evita parpadeos y confusión.
  const [draftPlatformColor, setDraftPlatformColor] = useState(platformColor);
  const [draftHeroImage, setDraftHeroImage] = useState(heroImageUrl || "");
  const [draftHeroImageDark, setDraftHeroImageDark] = useState(heroImageUrlDark || "");
  const [draftFavicon, setDraftFavicon] = useState(faviconUrl || "");
  const [draftLoginBanner, setDraftLoginBanner] = useState(loginBannerImage || "");
  const [draftPlatformName, setDraftPlatformName] = useState(platformName);
  const [draftHeading1, setDraftHeading1] = useState(landingHeadingLine1 || "");
  const [draftHeading2, setDraftHeading2] = useState(landingHeadingLine2 || "");
  const [draftChips, setDraftChips] = useState(landingChips || []);
  const [savingPlatformColor, setSavingPlatformColor] = useState(false);
  const [savingPlatformName, setSavingPlatformName] = useState(false);
  const [savingHeading, setSavingHeading] = useState(false);
  const [savingChips, setSavingChips] = useState(false);
  // Un flag de "busy" por cada asset slot (light/dark/favicon/loginBanner).
  // El handler `saveAsset` / `uploadAsset` lo usa para deshabilitar el
  // botón correspondiente sin bloquear los otros slots.
  const [busySlot, setBusySlot] = useState(null);
  useEffect(() => { setDraftPlatformColor(platformColor); }, [platformColor]);
  useEffect(() => { setDraftHeroImage(heroImageUrl || ""); }, [heroImageUrl]);
  useEffect(() => { setDraftHeroImageDark(heroImageUrlDark || ""); }, [heroImageUrlDark]);
  useEffect(() => { setDraftFavicon(faviconUrl || ""); }, [faviconUrl]);
  useEffect(() => { setDraftLoginBanner(loginBannerImage || ""); }, [loginBannerImage]);
  useEffect(() => { setDraftHeading1(landingHeadingLine1 || ""); }, [landingHeadingLine1]);
  useEffect(() => { setDraftHeading2(landingHeadingLine2 || ""); }, [landingHeadingLine2]);
  useEffect(() => { setDraftChips(landingChips || []); }, [landingChips]);
  useEffect(() => { setDraftPlatformName(platformName); }, [platformName]);

  // Tabla de slots: cada entrada agrupa cómo leer/escribir el valor en
  // el backend, el setter del context y el draft local. Mantener esto
  // centralizado evita duplicar 3 veces la misma lógica save/reset/upload.
  const ASSET_SLOTS = {
    light: {
      field: "hero_image_url",
      current: heroImageUrl,
      draft: draftHeroImage,
      setDraft: setDraftHeroImage,
      setCurrent: setHeroImageUrl,
      successLabel: "Imagen del landing (claro)",
    },
    dark: {
      field: "hero_image_url_dark",
      current: heroImageUrlDark,
      draft: draftHeroImageDark,
      setDraft: setDraftHeroImageDark,
      setCurrent: setHeroImageUrlDark,
      successLabel: "Imagen del landing (oscuro)",
    },
    favicon: {
      field: "favicon_url",
      current: faviconUrl,
      draft: draftFavicon,
      setDraft: setDraftFavicon,
      setCurrent: setFaviconUrl,
      successLabel: "Favicon",
    },
    loginBanner: {
      field: "login_banner_image",
      current: loginBannerImage,
      draft: draftLoginBanner,
      setDraft: setDraftLoginBanner,
      setCurrent: setLoginBannerImage,
      successLabel: "Banner del login",
    },
  };

  const saveHeading = async () => {
    setSavingHeading(true);
    try {
      await api.patch("/settings/platform", {
        landing_heading_line1: draftHeading1,
        landing_heading_line2: draftHeading2,
      });
      setLandingHeadingLine1(draftHeading1);
      setLandingHeadingLine2(draftHeading2);
      await reloadPlatformSettings();
      toast.success("Titular del landing actualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando titular");
    } finally {
      setSavingHeading(false);
    }
  };

  const updateChipField = (idx, field, value) => {
    setDraftChips((prev) => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const uploadChipImage = async (idx, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen excede 3 MB");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) updateChipField(idx, "image_url", data.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error subiendo");
    }
  };

  const saveChips = async () => {
    // Validación mínima: los 3 chips deben tener los 3 campos.
    const bad = draftChips.findIndex((c) => !c?.image_url?.trim() || !c?.title?.trim() || !c?.text?.trim());
    if (bad >= 0) {
      toast.error(`El chip ${bad + 1} tiene campos vacíos`);
      return;
    }
    setSavingChips(true);
    try {
      await api.patch("/settings/platform", { landing_chips: draftChips });
      setLandingChips(draftChips);
      await reloadPlatformSettings();
      toast.success("Chips actualizados");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando chips");
    } finally {
      setSavingChips(false);
    }
  };

  const savePlatformName = async () => {
    const name = draftPlatformName.trim();
    if (name.length < 2 || name.length > 32) {
      toast.error("El nombre debe tener entre 2 y 32 caracteres");
      return;
    }
    setSavingPlatformName(true);
    try {
      const { data } = await api.patch("/settings/platform", { platform_name: name });
      setPlatformName(data?.platform_name || name);
      await reloadPlatformSettings();
      toast.success("Nombre de plataforma actualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando nombre");
    } finally {
      setSavingPlatformName(false);
    }
  };

  const savePlatformColor = async () => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(draftPlatformColor)) {
      toast.error("Color inválido, usa formato #RRGGBB");
      return;
    }
    setSavingPlatformColor(true);
    try {
      await api.patch("/settings/platform", { primary_color: draftPlatformColor });
      setPlatformColor(draftPlatformColor);
      await reloadPlatformSettings();
      toast.success("Color de plataforma actualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando color");
    } finally {
      setSavingPlatformColor(false);
    }
  };

  const saveAsset = async (slotKey) => {
    const slot = ASSET_SLOTS[slotKey];
    setBusySlot(slotKey);
    try {
      await api.patch("/settings/platform", { [slot.field]: slot.draft });
      await reloadPlatformSettings();
      toast.success(`${slot.successLabel} actualizada`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando");
    } finally {
      setBusySlot(null);
    }
  };

  const resetAsset = async (slotKey) => {
    const slot = ASSET_SLOTS[slotKey];
    setBusySlot(slotKey);
    try {
      // El backend interpreta `""` como reset al default.
      const { data } = await api.patch("/settings/platform", { [slot.field]: "" });
      const fresh = data?.[slot.field] || null;
      slot.setCurrent(fresh);
      slot.setDraft(fresh || "");
      await reloadPlatformSettings();
      toast.success(`${slot.successLabel} restablecida al default`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error restableciendo");
    } finally {
      setBusySlot(null);
    }
  };

  const uploadAsset = async (slotKey, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen excede 3 MB");
      return;
    }
    const slot = ASSET_SLOTS[slotKey];
    setBusySlot(slotKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Reusa el endpoint genérico de upload de imágenes (mismo que se usa
      // para logos de empresa). Devuelve `{ path, url }`.
      const { data } = await api.post("/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url;
      if (!url) throw new Error("Upload sin URL");
      slot.setDraft(url);
      await api.patch("/settings/platform", { [slot.field]: url });
      await reloadPlatformSettings();
      toast.success(`${slot.successLabel} subida`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setBusySlot(null);
    }
  };

  const load = () => api.get("/companies/all").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/metrics/platform").then((r) => setM(r.data));
  }, []);

  const createCompany = async () => {
    try {
      await api.post("/companies", createForm);
      toast.success("Empresa creada");
      setCreateForm(EMPTY_CREATE);
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCompany = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar esta empresa?",
      description: "Los pedidos y productos asociados quedarán huérfanos. Esta acción no se puede deshacer.",
      confirmText: "Sí, eliminar",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/companies/${id}`);
    load();
  };

  const openEdit = (c) => {
    setEditCompany({ ...c });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editCompany.name,
        primary_color: editCompany.primary_color,
        logo_url: editCompany.logo_url,
        phone: editCompany.phone,
        whatsapp: editCompany.whatsapp,
        instagram: editCompany.instagram,
        facebook: editCompany.facebook,
        description: editCompany.description,
        active: editCompany.active,
      };
      await api.patch(`/companies/${editCompany.company_id}`, payload);
      toast.success("Empresa actualizada");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/companies/${c.company_id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openMetrics = async (c) => {
    setMetricsCompany(c);
    setMetricsOpen(true);
    setMetricsData(null);
    try {
      const { data } = await api.get(`/metrics/company/${c.company_id}`);
      setMetricsData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Super Admin</h1>
        <p className="text-neutral-500 text-sm mt-1">Gestión de empresas de la plataforma</p>

        {m && (
          <div className="mt-6 grid grid-cols-3 gap-3">
            <Metric title="Empresas" value={m.companies} />
            <Metric title="Clientes" value={m.clients} />
            <Metric title="Pedidos totales" value={m.orders} />
          </div>
        )}

        {/* Nombre comercial de la plataforma: aparece en TopBar, login,
            register, banner PWA. Para escenarios white-label, puedes
            renombrar toda la UI sin tocar código. */}
        <div className="mt-6 bg-white rounded-xl border border-neutral-200 p-4" data-testid="platform-name-card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-neutral-500" />
            <h2 className="font-semibold">Nombre comercial</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Texto mostrado en el header, login y registro. Entre 2 y 32 caracteres.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Nombre</Label>
              <Input
                className="h-11 mt-1"
                value={draftPlatformName}
                maxLength={32}
                onChange={(e) => setDraftPlatformName(e.target.value)}
                placeholder="Gas2GO"
                data-testid="platform-name-input"
              />
            </div>
            <Button
              onClick={savePlatformName}
              disabled={savingPlatformName || draftPlatformName.trim() === platformName || draftPlatformName.trim().length < 2}
              className="h-11 bg-black text-white hover:opacity-90 tap-fade"
              data-testid="save-platform-name-btn"
            >
              {savingPlatformName ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>

        {/* Color de plataforma: se usa en la UI mientras el usuario no ha
            seleccionado empresa (logout, registro, pantalla inicial de
            cliente). Cada empresa sigue teniendo su propio color, éste es
            solo el "neutral" de fallback. */}
        <div className="mt-4 bg-white rounded-xl border border-neutral-200 p-4" data-testid="platform-color-card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-neutral-500" />
            <h2 className="font-semibold">Color de plataforma</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Se aplica cuando el usuario está deslogueado o aún no ha seleccionado empresa.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  className="h-11 w-14 rounded-lg border border-neutral-200 cursor-pointer"
                  value={draftPlatformColor}
                  onChange={(e) => setDraftPlatformColor(e.target.value)}
                  data-testid="platform-color-picker"
                />
                <Input
                  className="h-11 w-32 font-mono"
                  value={draftPlatformColor}
                  onChange={(e) => setDraftPlatformColor(e.target.value)}
                  data-testid="platform-color-hex"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs">Preview</Label>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-11 w-11 rounded-lg border border-neutral-200" style={{ background: draftPlatformColor }} />
                <button
                  type="button"
                  className="h-11 px-4 rounded-lg text-sm font-semibold text-white"
                  style={{ background: draftPlatformColor }}
                >
                  Botón muestra
                </button>
              </div>
            </div>
            <Button
              onClick={savePlatformColor}
              disabled={savingPlatformColor || draftPlatformColor.toLowerCase() === platformColor.toLowerCase()}
              className="h-11 bg-black text-white hover:opacity-90 tap-fade"
              data-testid="save-platform-color-btn"
            >
              {savingPlatformColor ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>

        {/* Tres asset slots: hero claro, hero oscuro, favicon. Mismo
            componente reutilizado — la tabla `ASSET_SLOTS` decide el
            field del backend al que apunta cada uno. */}
        <AssetCard
          slotKey="light"
          slot={ASSET_SLOTS.light}
          busy={busySlot === "light"}
          anyBusy={busySlot !== null}
          onSave={() => saveAsset("light")}
          onReset={() => resetAsset("light")}
          onUpload={(f) => uploadAsset("light", f)}
          title="Imagen del landing — modo claro"
          description='Se muestra en la página principal cuando el tema está en "claro". Sube una imagen o pega una URL.'
          Icon={Sun}
          previewBg="bg-neutral-50"
          testid="platform-hero-light-card"
        />
        <AssetCard
          slotKey="dark"
          slot={ASSET_SLOTS.dark}
          busy={busySlot === "dark"}
          anyBusy={busySlot !== null}
          onSave={() => saveAsset("dark")}
          onReset={() => resetAsset("dark")}
          onUpload={(f) => uploadAsset("dark", f)}
          title="Imagen del landing — modo oscuro"
          description='Se muestra en la página principal cuando el tema está en "oscuro". Idealmente con fondo oscuro o wordmark en color claro.'
          Icon={Moon}
          previewBg="bg-neutral-900"
          testid="platform-hero-dark-card"
        />
        <AssetCard
          slotKey="favicon"
          slot={ASSET_SLOTS.favicon}
          busy={busySlot === "favicon"}
          anyBusy={busySlot !== null}
          onSave={() => saveAsset("favicon")}
          onReset={() => resetAsset("favicon")}
          onUpload={(f) => uploadAsset("favicon", f)}
          title="Favicon"
          description="Ícono de la pestaña del navegador. Se recomienda cuadrado (ej. 64×64) — PNG/ICO/SVG."
          Icon={Globe}
          previewBg="bg-neutral-50"
          testid="platform-favicon-card"
        />

        <AssetCard
          slotKey="loginBanner"
          slot={ASSET_SLOTS.loginBanner}
          busy={busySlot === "loginBanner"}
          anyBusy={busySlot !== null}
          onSave={() => saveAsset("loginBanner")}
          onReset={() => resetAsset("loginBanner")}
          onUpload={(f) => uploadAsset("loginBanner", f)}
          title="Banner del login"
          description="Imagen de fondo grande del panel izquierdo en la página de inicio de sesión."
          Icon={Globe}
          previewBg="bg-neutral-900"
          testid="platform-login-banner-card"
        />

        {/* Titular del landing (también usado como overlay del login).
            Línea 1 neutral, línea 2 en color primario (gancho). */}
        <div className="mt-4 bg-white rounded-xl border border-neutral-200 p-4" data-testid="landing-heading-card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-neutral-500" />
            <h2 className="font-semibold">Titular del landing / login</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            Dos líneas. La segunda se muestra en el color primario como gancho. Máx 40 caracteres cada una.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Línea 1 (neutral)</Label>
              <Input
                className="h-11 mt-1"
                maxLength={40}
                value={draftHeading1}
                onChange={(e) => setDraftHeading1(e.target.value)}
                placeholder="Tu gas,"
                data-testid="heading-line1-input"
              />
            </div>
            <div>
              <Label className="text-xs">Línea 2 (color primario)</Label>
              <Input
                className="h-11 mt-1"
                maxLength={40}
                value={draftHeading2}
                onChange={(e) => setDraftHeading2(e.target.value)}
                placeholder="cuando lo necesites."
                data-testid="heading-line2-input"
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              onClick={saveHeading}
              disabled={savingHeading || (draftHeading1 === landingHeadingLine1 && draftHeading2 === landingHeadingLine2)}
              className="h-11 bg-black text-white hover:opacity-90 tap-fade"
              data-testid="save-heading-btn"
            >
              {savingHeading ? "Guardando..." : "Guardar titular"}
            </Button>
          </div>
        </div>

        {/* Carrusel: 3 chips con imagen, título y texto. */}
        <div className="mt-4 bg-white rounded-xl border border-neutral-200 p-4" data-testid="landing-chips-card">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={16} className="text-neutral-500" />
            <h2 className="font-semibold">Chips del landing (carrusel)</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-3">
            3 tarjetas que rotan automáticamente en el hero. Cada una lleva imagen, título corto y texto grande.
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {draftChips.map((chip, idx) => (
              <div key={idx} className="border border-neutral-200 rounded-lg p-3" data-testid={`chip-editor-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-neutral-600">Chip {idx + 1}</span>
                  <label className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-800">
                    Subir imagen
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadChipImage(idx, f);
                        e.target.value = "";
                      }}
                      data-testid={`chip-${idx}-upload-input`}
                    />
                  </label>
                </div>
                <div className="h-20 rounded-md bg-neutral-100 overflow-hidden mb-2">
                  {chip?.image_url ? (
                    <img src={chip.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[11px] text-neutral-400">Sin imagen</div>
                  )}
                </div>
                <Label className="text-xs">URL imagen</Label>
                <Input
                  className="h-9 mt-1 font-mono text-[11px]"
                  value={chip?.image_url || ""}
                  onChange={(e) => updateChipField(idx, "image_url", e.target.value)}
                  placeholder="https://..."
                  data-testid={`chip-${idx}-image-input`}
                />
                <Label className="text-xs mt-2 block">Título</Label>
                <Input
                  className="h-9 mt-1"
                  maxLength={40}
                  value={chip?.title || ""}
                  onChange={(e) => updateChipField(idx, "title", e.target.value)}
                  placeholder="Entrega promedio"
                  data-testid={`chip-${idx}-title-input`}
                />
                <Label className="text-xs mt-2 block">Texto</Label>
                <Input
                  className="h-9 mt-1"
                  maxLength={40}
                  value={chip?.text || ""}
                  onChange={(e) => updateChipField(idx, "text", e.target.value)}
                  placeholder="28 min"
                  data-testid={`chip-${idx}-text-input`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              onClick={saveChips}
              disabled={savingChips}
              className="h-11 bg-black text-white hover:opacity-90 tap-fade"
              data-testid="save-chips-btn"
            >
              {savingChips ? "Guardando..." : "Guardar chips"}
            </Button>
          </div>
        </div>

        <div className="mt-8 flex justify-between items-center">
          <h2 className="font-display text-2xl font-bold">Empresas</h2>
          <Button onClick={() => setCreateOpen(true)} className="h-11 bg-black text-white hover:opacity-90 tap-fade" data-testid="add-company-btn">
            <Plus size={16} className="mr-2" />Nueva empresa
          </Button>
          <BottomSheet
            open={createOpen}
            onOpenChange={setCreateOpen}
            title="Crear empresa"
            description="Registra una nueva empresa y su administrador."
            testid="create-company-sheet"
          >
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nombre</Label><Input className="h-11" value={createForm.company.name} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, name: e.target.value } })} data-testid="new-company-name" /></div>
                  <div><Label>Slug (único)</Label><Input className="h-11" value={createForm.company.slug} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") } })} data-testid="new-company-slug" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Color primario</Label>
                    <div className="flex gap-2">
                      <input type="color" className="h-11 w-14 rounded-lg border border-neutral-200 cursor-pointer" value={createForm.company.primary_color} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, primary_color: e.target.value } })} />
                      <Input className="h-11" value={createForm.company.primary_color} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, primary_color: e.target.value } })} />
                    </div>
                  </div>
                  <div><Label>Logo URL</Label><Input className="h-11" value={createForm.company.logo_url} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, logo_url: e.target.value } })} placeholder="https://..." /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Teléfono</Label><Input className="h-11" value={createForm.company.phone} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, phone: e.target.value } })} /></div>
                  <div><Label>WhatsApp</Label><Input className="h-11" value={createForm.company.whatsapp} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, whatsapp: e.target.value } })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Instagram</Label><Input className="h-11" value={createForm.company.instagram} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, instagram: e.target.value } })} placeholder="@empresa" /></div>
                  <div><Label>Facebook</Label><Input className="h-11" value={createForm.company.facebook} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, facebook: e.target.value } })} placeholder="empresa" /></div>
                </div>
                <div><Label>Descripción</Label><Textarea value={createForm.company.description} onChange={(e) => setCreateForm({ ...createForm, company: { ...createForm.company, description: e.target.value } })} /></div>
                <div className="pt-4 border-t">
                  <p className="font-semibold mb-2">Administrador de la empresa</p>
                  <div className="space-y-3">
                    <div><Label>Nombre admin</Label><Input className="h-11" value={createForm.admin_name} onChange={(e) => setCreateForm({ ...createForm, admin_name: e.target.value })} data-testid="new-company-admin-name" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Email admin</Label><Input className="h-11" value={createForm.admin_email} onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })} data-testid="new-company-admin-email" /></div>
                      <div><Label>Contraseña</Label><Input type="password" className="h-11" value={createForm.admin_password} onChange={(e) => setCreateForm({ ...createForm, admin_password: e.target.value })} data-testid="new-company-admin-password" /></div>
                    </div>
                  </div>
                </div>
                <Button onClick={createCompany} className="w-full h-11 bg-black text-white tap-fade" data-testid="save-company-btn">Crear empresa</Button>
              </div>
            </div>
          </BottomSheet>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {list.map((c) => (
            <div key={c.company_id} className={`bg-white rounded-xl border p-4 ${c.active ? "border-neutral-200" : "border-neutral-200 opacity-60"}`} data-testid={`sa-company-${c.company_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex gap-3 items-start flex-1 min-w-0">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="h-11 w-11 rounded-full object-cover border border-neutral-200" />
                  ) : (
                    <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: c.primary_color }}>
                      {c.name[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{c.name}</p>
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: c.primary_color }} />
                    </div>
                    <p className="text-xs text-neutral-500">/{c.slug}</p>
                    {c.description && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{c.description}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-neutral-500">
                      {c.phone && <span className="flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                      {c.instagram && <span className="flex items-center gap-1"><Instagram size={11} />{c.instagram}</span>}
                      {c.facebook && <span className="flex items-center gap-1"><Facebook size={11} />{c.facebook}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">{c.active ? "Activa" : "Pausada"}</span>
                    <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-active-${c.company_id}`} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-neutral-100">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openMetrics(c)} data-testid={`metrics-${c.company_id}`}>
                  <BarChart3 size={14} className="mr-1" />Métricas
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(c)} data-testid={`edit-${c.company_id}`}>
                  <Pencil size={14} className="mr-1" />Editar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeCompany(c.company_id)} data-testid={`delete-${c.company_id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Edit dialog */}
        <BottomSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          title={`Editar ${editCompany?.name || ""}`}
          description="Actualiza la información y branding de la empresa."
          testid="edit-company-sheet"
        >
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto max-h-[70vh]">
            {editCompany && (
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input className="h-11" value={editCompany.name || ""} onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })} data-testid="edit-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Color primario</Label>
                    <div className="flex gap-2">
                      <input type="color" className="h-11 w-14 rounded-lg border border-neutral-200 cursor-pointer" value={editCompany.primary_color || "#0A0A0A"} onChange={(e) => setEditCompany({ ...editCompany, primary_color: e.target.value })} />
                      <Input className="h-11" value={editCompany.primary_color || ""} onChange={(e) => setEditCompany({ ...editCompany, primary_color: e.target.value })} />
                    </div>
                  </div>
                  <div><Label>Logo URL</Label><Input className="h-11" value={editCompany.logo_url || ""} onChange={(e) => setEditCompany({ ...editCompany, logo_url: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Teléfono</Label><Input className="h-11" value={editCompany.phone || ""} onChange={(e) => setEditCompany({ ...editCompany, phone: e.target.value })} /></div>
                  <div><Label>WhatsApp</Label><Input className="h-11" value={editCompany.whatsapp || ""} onChange={(e) => setEditCompany({ ...editCompany, whatsapp: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Instagram</Label><Input className="h-11" value={editCompany.instagram || ""} onChange={(e) => setEditCompany({ ...editCompany, instagram: e.target.value })} /></div>
                  <div><Label>Facebook</Label><Input className="h-11" value={editCompany.facebook || ""} onChange={(e) => setEditCompany({ ...editCompany, facebook: e.target.value })} /></div>
                </div>
                <div><Label>Descripción</Label><Textarea value={editCompany.description || ""} onChange={(e) => setEditCompany({ ...editCompany, description: e.target.value })} /></div>
                <div className="flex items-center gap-3 pt-2">
                  <Switch checked={!!editCompany.active} onCheckedChange={(v) => setEditCompany({ ...editCompany, active: v })} data-testid="edit-active-switch" />
                  <Label className="m-0">Empresa activa</Label>
                </div>
                <Button onClick={saveEdit} className="w-full h-11 bg-black text-white tap-fade" data-testid="save-edit-btn">Guardar cambios</Button>
              </div>
            )}
          </div>
        </BottomSheet>

        {/* Metrics dialog */}
        <BottomSheet
          open={metricsOpen}
          onOpenChange={setMetricsOpen}
          title={`Métricas — ${metricsCompany?.name || ""}`}
          description="Rendimiento de la empresa."
          testid="metrics-sheet"
        >
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            {!metricsData ? (
              <p className="text-sm text-neutral-500">Cargando...</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Metric title="Pedidos hoy" value={metricsData.orders_today} />
                <Metric title="Total pedidos" value={metricsData.total_orders} />
                <Metric title="Ingresos" value={money(metricsData.revenue)} />
                <Metric title="Unidades de Reparto" value={metricsData.drivers} />
                <Metric title="Productos activos" value={metricsData.products} />
              </div>
            )}
          </div>
        </BottomSheet>
      </main>
    </div>
  );
}

function Metric({ title, value }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5">
      <p className="text-sm text-neutral-500">{title}</p>
      <p className="font-display font-extrabold text-3xl mt-1">{value}</p>
    </div>
  );
}

/**
 * Card reutilizable para editar un asset de plataforma (hero claro,
 * hero oscuro, favicon). Muestra preview, URL editable, upload y
 * botones de guardar/restablecer. Todas las acciones se delegan al
 * parent via las props `onSave`/`onReset`/`onUpload` para mantener
 * la lógica de estado centralizada arriba.
 */
function AssetCard({ slotKey, slot, busy, anyBusy, onSave, onReset, onUpload, title, description, Icon, previewBg, testid }) {
  const hasChanges = slot.draft !== (slot.current || "");
  return (
    <div className="mt-4 bg-white rounded-xl border border-neutral-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-neutral-500" />
        <h2 className="font-semibold">{title}</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-3">{description}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <Label className="text-xs">Preview</Label>
          <div className={`mt-1 h-20 w-40 rounded-lg border border-neutral-200 ${previewBg} flex items-center justify-center overflow-hidden`}>
            {slot.draft ? (
              <img
                src={slot.draft}
                alt="asset"
                className="max-h-full max-w-full object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <span className="text-[11px] text-neutral-400">Sin imagen</span>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs">URL</Label>
          <Input
            className="h-11 mt-1 font-mono text-xs"
            value={slot.draft}
            onChange={(e) => slot.setDraft(e.target.value)}
            placeholder="/asset.png"
            data-testid={`${testid}-url`}
          />
        </div>
        <div>
          <Label className="text-xs">Subir archivo</Label>
          <label
            className={`mt-1 h-11 px-4 inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium cursor-pointer hover:bg-neutral-50 ${busy ? "opacity-60 pointer-events-none" : ""}`}
            data-testid={`${testid}-upload-label`}
          >
            {busy ? "Subiendo..." : "Elegir archivo"}
            <input
              type="file"
              accept={slotKey === "favicon" ? "image/png,image/x-icon,image/svg+xml,image/jpeg" : "image/png,image/jpeg,image/webp"}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                e.target.value = "";
              }}
              data-testid={`${testid}-upload-input`}
            />
          </label>
        </div>
        <Button
          onClick={onSave}
          disabled={anyBusy || !hasChanges}
          className="h-11 bg-black text-white hover:opacity-90 tap-fade"
          data-testid={`${testid}-save-btn`}
        >
          {busy ? "Guardando..." : "Guardar"}
        </Button>
        <Button
          onClick={onReset}
          disabled={anyBusy}
          variant="outline"
          className="h-11 tap-fade"
          data-testid={`${testid}-reset-btn`}
        >
          Restablecer default
        </Button>
      </div>
    </div>
  );
}
